// ─────────────────────────────────────────────────────────────────────────────
// graphDock — the hard-left rail, collapsed by default.
//
// It replaces three permanently open overlay regions: the stats block and the
// filter panel stacked top-left, the search-and-breadcrumb bar stretched across
// the middle of the scene, and the inspector pinned top-right. Between them they
// occupied most of the frame before the owner had touched anything.
//
// THE RAIL OVERLAYS; IT NEVER RESERVES LAYOUT. It is absolutely positioned
// inside the graph viewport, so opening a panel does not resize the canvas,
// does not change the camera aspect, and does not trigger a buffer rebuild.
//
// pointer-events are OFF on the container and ON only for the rail and the open
// panel, so a drag that begins on empty space next to the rail still orbits the
// graph rather than dying on an invisible box.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { GraphDockTab } from './graphUiState';

const RAIL_WIDTH = 42;
const RAIL_WIDTH_TOUCH = 44;
const PANEL_WIDTH = 300;

export interface GraphDockProps {
  open: boolean;
  activeTab: GraphDockTab;
  /** A dot on the Inspector icon when something is selected but the dock is shut. */
  hasSelection: boolean;
  coarsePointer: boolean;
  onToggle: (tab: GraphDockTab) => void;
  children: React.ReactNode;
}

interface TabDef {
  id: GraphDockTab;
  label: string;
  icon: React.ReactNode;
}

// Inline strokes rather than an icon dependency: five glyphs do not justify a
// package, and these inherit currentColor for the active/inactive states.
const ICONS: TabDef[] = [
  {
    // STRUCTURE leads the rail deliberately. The owner's complaint was that the
    // only way to learn what exists was to fly around the galaxy hunting
    // particles; the index that answers "what is in this OS" belongs first.
    id: 'structure',
    label: 'Structure',
    icon: (
      <>
        <line x1="4" y1="4" x2="4" y2="14" />
        <line x1="4" y1="6" x2="9" y2="6" />
        <line x1="4" y1="10" x2="9" y2="10" />
        <line x1="4" y1="14" x2="9" y2="14" />
        <circle cx="11.5" cy="6" r="1.6" />
        <circle cx="11.5" cy="10" r="1.6" />
        <circle cx="11.5" cy="14" r="1.6" />
      </>
    ),
  },
  {
    id: 'search',
    label: 'Search',
    icon: (
      <>
        <circle cx="7.5" cy="7.5" r="4.5" />
        <line x1="11" y1="11" x2="15" y2="15" />
      </>
    ),
  },
  {
    id: 'filters',
    label: 'Filters',
    icon: (
      <>
        <line x1="2.5" y1="4.5" x2="15.5" y2="4.5" />
        <line x1="5" y1="9" x2="13" y2="9" />
        <line x1="7.5" y1="13.5" x2="10.5" y2="13.5" />
      </>
    ),
  },
  {
    id: 'display',
    label: 'Display',
    icon: (
      <>
        <line x1="2.5" y1="6" x2="15.5" y2="6" />
        <circle cx="6.5" cy="6" r="2" />
        <line x1="2.5" y1="12" x2="15.5" y2="12" />
        <circle cx="11.5" cy="12" r="2" />
      </>
    ),
  },
  {
    id: 'inspector',
    label: 'Inspector',
    icon: (
      <>
        <rect x="3" y="3" width="12" height="12" rx="1.5" />
        <line x1="6" y1="7.5" x2="12" y2="7.5" />
        <line x1="6" y1="10.5" x2="10" y2="10.5" />
      </>
    ),
  },
  {
    id: 'dev',
    label: 'Dev',
    icon: (
      <>
        <polyline points="4,6 7,9 4,12" />
        <line x1="9.5" y1="12" x2="14" y2="12" />
      </>
    ),
  },
];

export default function GraphDock({
  open, activeTab, hasSelection, coarsePointer, onToggle, children,
}: GraphDockProps): React.ReactElement {
  const railWidth = coarsePointer ? RAIL_WIDTH_TOUCH : RAIL_WIDTH;

  return (
    <div style={containerStyle} data-open={open ? 'true' : 'false'}>
      <nav style={{ ...railStyle, width: railWidth }} aria-label="Graph controls">
        {ICONS.map((tab) => {
          const active = open && activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              title={tab.label}
              aria-label={tab.label}
              aria-pressed={active}
              onClick={() => onToggle(tab.id)}
              style={{
                ...railButtonStyle,
                width: railWidth,
                height: railWidth,
                color: active ? '#cfe6ff' : '#5c7185',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none"
                stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                {tab.icon}
              </svg>
              {tab.id === 'inspector' && hasSelection && !active && <span style={dotStyle} />}
              {active && <span style={activeBarStyle} />}
            </button>
          );
        })}
      </nav>

      <section
        style={{
          ...panelStyle,
          left: railWidth,
          width: `min(${PANEL_WIDTH}px, 86vw)`,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
        aria-hidden={!open}
      >
        {open ? children : null}
      </section>
    </div>
  );
}

const FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  zIndex: 30,
  // OFF here, ON on the rail and the open panel — see the header.
  pointerEvents: 'none',
  display: 'flex',
};

const railStyle: React.CSSProperties = {
  position: 'relative',
  height: '100%',
  pointerEvents: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: 10,
  gap: 2,
  background: 'rgba(3, 8, 16, 0.76)',
  borderRight: '1px solid rgba(80, 180, 235, 0.15)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};

const railButtonStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  color: '#5c7185',
  transition: 'color 140ms ease',
};

const activeBarStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 8,
  bottom: 8,
  width: 2,
  background: '#2EA8FF',
};

const dotStyle: React.CSSProperties = {
  position: 'absolute',
  top: 9,
  right: 9,
  width: 5,
  height: 5,
  borderRadius: 3,
  background: '#2EA8FF',
};

const panelStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  background: 'rgba(3, 8, 16, 0.88)',
  borderRight: '1px solid rgba(80, 180, 235, 0.17)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  transition: 'transform 180ms ease, opacity 160ms ease',
  overflowY: 'auto',
  padding: '14px 14px 24px',
  fontFamily: FONT,
  fontSize: 10.5,
  color: '#9fb4cc',
};

export const dockPanelTitleStyle: React.CSSProperties = {
  color: '#4e6076',
  letterSpacing: 1.6,
  textTransform: 'uppercase',
  fontSize: 9,
  marginBottom: 10,
};
